/**
 * Dizayn-sistemaning asosiy komponentlari — NF-603, PDP Design System.
 *
 * Manba: design.pdp.uz. Qoidalar: karta radius-24 va RAMKASIZ (faqat ko'k
 * soya), barcha tugma va nishonlar PILL, Label uslubi 12/800/+0.12em
 * UPPERCASE, 12px dan kichik matn yo'q.
 *
 * QOIDA: bu fayldagi hech bir komponent ma'lumot o'qimaydi va API ga
 * murojaat qilmaydi — ular faqat ko'rinish. Ma'lumot sahifadan `props`
 * orqali keladi.
 */

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Inbox } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/** Sinf nomlarini birlashtiradi (`clsx` o'rniga — bitta funksiya yetarli). */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// ─────────────────────────────────────────────────────────────────────────────
// Matn va sarlavha
// ─────────────────────────────────────────────────────────────────────────────

/** «Kicker» — DS Label uslubi: 12px / 800 / +0.12em / UPPERCASE / muted. */
export function Kicker({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("text-xs font-extrabold uppercase tracking-kicker text-muted", className)}>
      {children}
    </div>
  );
}

/**
 * PDP rule — sarlavha ostidagi 4px uch rangli chiziq (31/36/33 proporsiya).
 * Ekran boshiga BIR marta ishlatiladi.
 */
export function PdpRule({ className }: { className?: string }) {
  return (
    <div className={cx("flex h-1 w-24 overflow-hidden rounded-full", className)} aria-hidden="true">
      <span className="h-full w-[31%] bg-[#1B56DB]" />
      <span className="h-full w-[36%] bg-[#1445C4]" />
      <span className="h-full w-[33%] bg-[#2748B6]" />
    </div>
  );
}

/** Sahifa sarlavhasi (H1 uslubi) + PDP rule + ixtiyoriy amal tugmalari. */
export function PageHeader({
  kicker,
  title,
  description,
  actions,
}: {
  kicker?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {kicker ? <Kicker className="mb-2">{kicker}</Kicker> : null}
        <h1 className="truncate text-[28px] font-black leading-tight tracking-[-0.035em] text-ink sm:text-[40px]">
          {title}
        </h1>
        <PdpRule className="mt-3" />
        {description ? <p className="mt-3 max-w-2xl text-[15px] text-body">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Yuzalar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Karta — PDP DS: radius 24, oq fon, ko'k soya, RAMKA YO'Q.
 * `lift` — FAQAT bosiladigan karta uchun: hover da mayin ko'tarilish.
 */
export function Card({
  children,
  className,
  lift,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode; lift?: boolean }) {
  return (
    <div
      className={cx("rounded-panel bg-surface shadow-card", lift && "card-lift", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Kartaning sarlavha qismi. */
export function CardHeader({
  title,
  hint,
  actions,
}: {
  title: string;
  hint?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-hairline px-6 py-4">
      <div className="min-w-0">
        <h2 className="text-[19px] font-extrabold tracking-[-0.02em] text-ink">{title}</h2>
        {hint ? <p className="mt-0.5 text-[13px] font-semibold text-tertiary">{hint}</p> : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  );
}

/**
 * KPI kartasi — PDP DS: (ixtiyoriy ikon-kvadrat 38×38, radius-12) + delta
 * nishon (balandligi 28px) yuqori qatorda, 38/900 qiymat, Label pastda.
 * Har ekranda BITTA bosh metrika bo'lishi qoidasini sahifa o'zi saqlaydi.
 */
export function KpiCard({
  label,
  value,
  delta,
  deltaTone = "info",
  hint,
  icon: IconComponent,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Masalan «+12%» yoki ikon + matn — bo'sh bo'lsa nishon chiqmaydi. */
  delta?: ReactNode;
  deltaTone?: "success" | "warning" | "error" | "info" | "hot";
  hint?: ReactNode;
  /** DS ikon-kvadrat (38×38, brand50/brand600) — berilmasa Label yuqorida chiqadi. */
  icon?: LucideIcon;
  className?: string;
}) {
  return (
    <Card className={cx("px-6 py-6", className)}>
      <div className="mb-4 flex items-center justify-between gap-2">
        {IconComponent ? (
          <span className="flex size-[38px] shrink-0 items-center justify-center rounded-icon bg-brand-50 text-brand-600">
            <IconComponent className="size-[19px]" strokeWidth={1.9} aria-hidden="true" />
          </span>
        ) : (
          <Kicker>{label}</Kicker>
        )}
        {delta ? (
          <Badge
            tone={deltaTone}
            className="!h-7 !rounded-full !px-[11px] !text-[12.5px] !font-extrabold"
          >
            {delta}
          </Badge>
        ) : null}
      </div>
      <div className="text-[38px] font-black leading-none tracking-[-0.04em] text-ink">{value}</div>
      {IconComponent ? (
        <div className="mt-1.5 text-[14px] font-semibold text-tertiary">{label}</div>
      ) : null}
      {hint ? <div className="mt-2 text-[13px] font-semibold text-tertiary">{hint}</div> : null}
    </Card>
  );
}

/** Semantik ikon-kvadrat ranglari — `StatMini`/hisoblagich katakchalari uchun. */
const STAT_TONE: Record<string, string> = {
  brand: "bg-brand-50 text-brand-600",
  success: "bg-good-soft text-good",
  warning: "bg-warn-soft text-warn-ink",
  error: "bg-bad-soft text-bad",
  info: "bg-info-soft text-info-ink",
};

/**
 * Ixcham statistika katakchasi — ikon-kvadrat (28px) + qiymat (22/800) +
 * label (11/700 UPPERCASE, muted). `KpiCard`dan farqli: delta yo'q, zich
 * to'rlarda (masalan «Qo'ng'iroqlar oqimi» hisoblagichlari) ishlatiladi.
 */
export function StatMini({
  icon: IconComponent,
  tone = "brand",
  value,
  label,
  className,
}: {
  icon: LucideIcon;
  tone?: keyof typeof STAT_TONE;
  value: ReactNode;
  /** Label + ixtiyoriy `InfoHint` shu yerga birga beriladi. */
  label: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("flex items-center gap-2.5 bg-surface px-5 py-4", className)}>
      <span
        className={cx(
          "flex size-7 shrink-0 items-center justify-center rounded-icon",
          STAT_TONE[tone],
        )}
      >
        <IconComponent className="size-[15px]" strokeWidth={1.85} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="text-[22px] font-extrabold leading-none tabular-nums text-ink">{value}</div>
        <div className="mt-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-kicker text-muted">
          {label}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tugma
// ─────────────────────────────────────────────────────────────────────────────

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

/** PDP DS tugma variantlari: Primary / Secondary / Ghost / Destructive. */
const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-600 text-white shadow-cta hover:bg-brand-700 hover:shadow-raised " +
    "active:bg-brand-800 active:shadow-none disabled:opacity-45",
  secondary:
    "bg-surface text-navy border border-border hover:bg-brand-50 hover:border-brand-100 " +
    "disabled:opacity-45",
  ghost: "bg-transparent text-brand-600 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-45",
  danger: "bg-bad-solid text-white hover:bg-bad disabled:opacity-45",
};

/** Balandliklar: 38 / 44 / 48 / 58. Hammasi pill. */
const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: "h-[38px] px-4 text-[13px]",
  md: "h-11 px-5 text-[15px]",
  lg: "h-12 px-[26px] text-[15px]",
  xl: "h-[58px] px-8 text-base",
};

export function Button({
  variant = "secondary",
  size = "md",
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-all",
        // Klaviatura bilan boshqarganda fokus KO'RINIB turishi shart.
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
        "disabled:cursor-not-allowed",
        BUTTON_VARIANT[variant],
        BUTTON_SIZE[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Nishon (badge) va holat
// ─────────────────────────────────────────────────────────────────────────────

/** Semantik uchliklar (fill / fg / solid dot) — DS badge ranglari. */
const BADGE_TONE: Record<string, { pill: string; dot: string }> = {
  success: { pill: "bg-good-soft text-good", dot: "bg-good-solid" },
  warning: { pill: "bg-warn-soft text-warn-ink", dot: "bg-warn-solid" },
  error: { pill: "bg-bad-soft text-bad", dot: "bg-bad-solid" },
  info: { pill: "bg-info-soft text-info-ink", dot: "bg-brand-600" },
  hot: { pill: "bg-hot-soft text-hot", dot: "bg-hot-solid" },
};

/**
 * Nishon — DOIM pill. Rang ikki yo'l bilan beriladi:
 *   - `tone` — DS semantik uchligi (fill/fg + solid dot);
 *   - `className`/`dot` — eski API, uslub `tokens.ts` dan keladi.
 * Rang yolg'iz status tashimaydi — doim matn (va imkon bo'lsa dot) bilan.
 */
export function Badge({
  children,
  className,
  dot,
  tone,
}: {
  children: ReactNode;
  className?: string;
  dot?: string;
  tone?: keyof typeof BADGE_TONE;
}) {
  const toneStyle = tone ? BADGE_TONE[tone] : undefined;
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ring-transparent",
        toneStyle?.pill,
        className,
      )}
    >
      {toneStyle ? <span className={cx("size-1.5 rounded-full", toneStyle.dot)} /> : null}
      {!toneStyle && dot ? <span className={cx("size-1.5 rounded-full", dot)} /> : null}
      {children}
    </span>
  );
}

/** «Jonli» nuqta — belgi metaforasi (puls). Faqat davom etayotgan jarayon uchun. */
export function LiveDot({ className }: { className?: string }) {
  return (
    <span className={cx("relative flex size-2", className)}>
      <span className="absolute inline-flex size-full animate-beat rounded-full bg-brand-300" />
      <span className="relative inline-flex size-2 rounded-full bg-brand-600" />
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bo'sh holat, yuklanish, xato
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bo'sh holat — DS: brand50 ikon-kvadrat (radius-12) + sabab + keyingi qadam.
 * «Ma'lumot yo'q» yozuvi foydalanuvchini boshi berk ko'chaga olib boradi.
 */
export function EmptyState({
  title,
  description,
  action,
  icon: IconComponent = Inbox,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /** Kontekstga mos lucide ikon — berilmasa umumiy Inbox. */
  icon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-4 flex size-12 items-center justify-center rounded-icon bg-brand-50 text-brand-600">
        <IconComponent className="size-6" strokeWidth={1.85} aria-hidden="true" />
      </div>
      <p className="text-[15px] font-bold text-ink">{title}</p>
      {description ? (
        <p className="mt-1 max-w-sm text-[13px] font-semibold text-tertiary">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Yuklanish o'rni — sahifa «sakramasligi» uchun haqiqiy o'lchamda. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cx("relative overflow-hidden rounded-icon bg-[#EBECF0]", className)}>
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-[#F7F8FA] to-transparent" />
    </div>
  );
}

/**
 * Xato paneli — DS error uchligi (fill/fg).
 *
 * `traceId` KO'RSATILADI: NF-506 bo'yicha foydalanuvchi qo'llab-quvvatlashga
 * aynan shu ID ni aytadi va jurnal bo'yicha so'rov 15 daqiqada topiladi.
 */
export function ErrorPanel({
  message,
  traceId,
  onRetry,
}: {
  message: string;
  traceId?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div className="rounded-tile bg-bad-soft px-5 py-4">
      <p className="text-[15px] font-semibold text-bad">{message}</p>
      {traceId ? (
        <p className="mt-1 font-mono text-xs text-tertiary">
          So'rov ID: <span className="select-all">{traceId}</span>
        </p>
      ) : null}
      {onRetry ? (
        <Button size="sm" variant="secondary" className="mt-3" onClick={onRetry}>
          Qayta urinish
        </Button>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Jadval
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Jadval o'ramchisi — kichik ekranda gorizontal siljiydi.
 *
 * `maxHeight` berilsa jadval vertikal ham siljiydi va SARLAVHA YOPIShIB
 * qoladi: sakkiz ustunli jadvalda sarlavha yo'qolsa foydalanuvchi qaysi
 * ustun nima ekanini eslab qolishi kerak bo'ladi.
 */
export function TableWrap({ children, maxHeight }: { children: ReactNode; maxHeight?: number }) {
  return (
    <div
      className="overflow-x-auto"
      style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}
    >
      <table
        className={cx(
          "w-full min-w-[720px] border-collapse text-[15px]",
          // Qator balandligi ~60px (Td py-[18px] orqali, DS jadval spetsi) — zebra fon + hover.
          "[&_tbody_tr:nth-child(even)]:bg-canvas [&_tbody_tr:hover]:bg-brand-50/40",
        )}
      >
        {children}
      </table>
    </div>
  );
}

/**
 * Jadval sarlavhasi — DS Label uslubi: 12/800, +0.12em, UPPERCASE, muted.
 * Fon `canvas` (#FAFBFE) — DS jadval spetsi. Doim `sticky`: uzun ro'yxatda
 * ustun nomi ko'rinib turadi.
 */
export function Th({
  children,
  className,
  align = "left",
}: {
  children: ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}) {
  return (
    <th
      className={cx(
        "sticky top-0 z-10 border-b border-border bg-canvas px-6 py-3.5",
        "text-xs font-extrabold uppercase tracking-kicker text-muted",
        align === "right" && "text-right",
        align === "center" && "text-center",
        align === "left" && "text-left",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  align = "left",
}: {
  children: ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}) {
  return (
    <td
      className={cx(
        "border-b border-hairline px-6 py-[18px] text-body",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
    >
      {children}
    </td>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ta'rif belgisi (ⓘ)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Metrika ta'rifi — «ⓘ» belgisiga sichqoncha olib borilganda chiqadi.
 *
 * NIMA UCHUN MAJBURIY: bir xil nomdagi metrikaning ikki xil ta'rifi —
 * mahsulotga bo'lgan ishonchni yo'q qiladigan eng katta xato. Ta'riflar
 * manbai — TZ Ilova C «yagona formulalar lug'ati».
 *
 * `title` atributi EMAS, o'z paneli: `title` mobil qurilmada umuman
 * ko'rinmaydi va uni klaviatura bilan ocholmaysiz.
 */
/**
 * Chekka-himoyali tooltip: markazlashtirilgan `left-1/2` naqsh ekran
 * chetiga yaqin turgan `i` tugmalarida (mas. eng o'ngdagi KPI kartasi)
 * tooltipni viewport tashqarisiga chiqarib, gorizontal scroll keltirib
 * chiqargani CDP orqali o'lchab tasdiqlangan (Dashboard, 1280px kenglik).
 * Tugma joylashuvi bir marta (mount + resize da) o'lchanadi va shunga
 * qarab markaz / chap / o'ng tomonga tekislanadi.
 */
export function InfoHint({ text, label }: { text: string; label?: string }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [align, setAlign] = useState<"center" | "left" | "right">("center");

  useEffect(() => {
    const HALF_TOOLTIP_PX = 112; // w-56 (224px) yarmi
    const EDGE_MARGIN_PX = 16;

    function measure(): void {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      if (rect.left < HALF_TOOLTIP_PX + EDGE_MARGIN_PX) {
        setAlign("left");
      } else if (window.innerWidth - rect.right < HALF_TOOLTIP_PX + EDGE_MARGIN_PX) {
        setAlign("right");
      } else {
        setAlign("center");
      }
    }

    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <span className="group relative inline-flex align-middle">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label ?? "Ta'rif"}
        className="flex size-[18px] items-center justify-center rounded-full border border-border text-xs font-bold text-muted transition-colors hover:border-brand-600 hover:text-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
      >
        i
      </button>
      <span
        role="tooltip"
        className={cx(
          "pointer-events-none absolute bottom-full z-30 mb-1.5 w-56 rounded-tile bg-navy px-3 py-2 text-xs leading-relaxed text-white opacity-0 shadow-modal transition-opacity group-focus-within:opacity-100 group-hover:opacity-100",
          align === "center" && "left-1/2 -translate-x-1/2",
          align === "left" && "left-0",
          align === "right" && "right-0",
        )}
      >
        {text}
      </span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Forma elementlari
// ─────────────────────────────────────────────────────────────────────────────

/**
 * DS input: balandlik 52 (design.pdp.uz manba qiymati — butun ilovada
 * BITTA qiymatga izchillashtirilgan, eski 48px o'rniga), radius 16,
 * focus — brand chegara + halqa.
 */
const FIELD_BASE =
  "h-[52px] w-full rounded-tile border border-border bg-surface px-[18px] text-[15px] text-ink " +
  "placeholder:text-muted focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/25 " +
  "disabled:cursor-not-allowed disabled:bg-subtle";

export function Input({ className, ...rest }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(FIELD_BASE, className)} {...rest} />;
}

export function Select({
  className,
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(FIELD_BASE, "pr-8", className)} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({
  className,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cx(FIELD_BASE, "h-auto min-h-[80px] py-2.5 leading-relaxed", className)}
      {...rest}
    />
  );
}

/** Maydon o'ramchisi — yorliq, izoh va xato bir joyda. */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-semibold text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-[13px] font-semibold text-bad">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[13px] font-semibold text-tertiary">{hint}</p>
      ) : null}
    </div>
  );
}
